import { Link, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft,
  Bot,
  Database,
  HardDrive,
  HeartPulse,
  Mail,
  Server,
  Activity,
  Shield,
  Terminal,
  Users,
} from 'lucide-react'
import { AISettingsCard } from '@/components/admin/AISettingsCard'
import {
  NasBackupSettingsCard,
  ServerBackupSettingsCard,
} from '@/components/admin/BackupSettingsCard'
import { ExchangeSettingsCard } from '@/components/admin/ExchangeSettingsCard'
import { PostureThresholdsSettingsCard } from '@/components/admin/PostureThresholdsSettingsCard'
import { SecuritySettingsCard } from '@/components/admin/SecuritySettingsCard'
import { ZabbixSettingsCard } from '@/components/admin/ZabbixSettingsCard'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

const SECTIONS = [
  {
    id: 'security',
    title: 'Security',
    description: 'Site-wide two-factor authentication default for portal accounts.',
    icon: Shield,
  },
  {
    id: 'rmm',
    title: 'RMM scripts',
    description: 'View when each Atera ingest script last sent data to the portal.',
    icon: Terminal,
    href: '/admin/ingest',
  },
  {
    id: 'posture',
    title: 'Posture thresholds',
    description: 'Memory usage, installed RAM, disk, CPU, and BIOS age for Review and Action needed.',
    icon: HeartPulse,
  },
  {
    id: 'ai',
    title: 'AI assistant',
    description: 'Provider, model, API key, and playbooks for the Assistant.',
    icon: Bot,
  },
  {
    id: 'server-backup',
    title: 'Server Backup',
    description: 'Altaro hostnames and expected guest VMs for the estate Backup page.',
    icon: HardDrive,
  },
  {
    id: 'nas-backup',
    title: 'NAS Backup',
    description: 'Synology NAS hostnames for Hyper Backup and Cloud Sync monitoring.',
    icon: Server,
  },
  {
    id: 'zabbix',
    title: 'Zabbix',
    description: 'Host-list sync for the coverage report and Open in Zabbix on a device.',
    icon: Activity,
  },
  {
    id: 'exchange',
    title: 'Exchange health',
    description: 'Automatic services excluded from the Exchange health check when stopped.',
    icon: Mail,
  },
  {
    id: 'server-config',
    title: 'Server configuration',
    description: 'Atera, ThreatDown, database, and sync schedule configured in .env.',
    icon: Database,
  },
]

function SettingsTile({ to, title, description, icon: Icon, className }) {
  return (
    <Link
      to={to}
      className={cn(
        'rounded-xl border border-border bg-card p-6 shadow-sm transition',
        'hover:border-primary/40 hover:bg-card hover:shadow-md',
        className,
      )}
    >
      <div className="mb-4">
        <Icon className="h-7 w-7 text-primary" strokeWidth={1.75} aria-hidden />
      </div>
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{description}</p>
    </Link>
  )
}

function SettingsOverview() {
  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-card px-6 py-8 shadow-sm sm:px-8">
        <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
          Settings
        </h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          Configure security, posture thresholds, backup hosts, and the AI assistant.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {SECTIONS.map((section) => (
          <SettingsTile
            key={section.id}
            to={section.href || `/admin/settings?section=${section.id}`}
            title={section.title}
            description={section.description}
            icon={section.icon}
          />
        ))}
        <SettingsTile
          to="/admin/users"
          title="Users"
          description="Portal accounts, roles, and password resets."
          icon={Users}
        />
      </div>
    </div>
  )
}

function ServerConfigPanel() {
  return (
    <div className="space-y-4 text-sm text-muted-foreground">
      <p>
        Atera, ThreatDown sync, database credentials, and sync schedule are configured in{' '}
        <code className="rounded bg-muted px-1 text-foreground">
          /opt/atera-reports-dev/backend/.env
        </code>
        , then restart the API service:
      </p>
      <pre className="overflow-x-auto rounded-lg border border-border bg-muted/30 p-4 text-xs text-foreground">
{`systemctl --user restart atera-portal-api-dev

# Key variables:
ATERA_API_KEY=
THREATDOWN_CLIENT_ID=
THREATDOWN_CLIENT_SECRET=
SYNC_SCHEDULE_HOUR=2
ALLOWED_ORIGINS=https://aterareports-d.bekat.co.uk`}
      </pre>
    </div>
  )
}

function SectionDetail({ section }) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <Button asChild variant="ghost" size="sm" className="-ml-2 h-8 px-2 text-muted-foreground">
            <Link to="/admin/settings">
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              Back to Settings
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">{section.title}</h1>
          <p className="text-sm text-muted-foreground">{section.description}</p>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4 shadow-sm sm:p-6">
        {section.id === 'security' && <SecuritySettingsCard embedded />}
        {section.id === 'posture' && <PostureThresholdsSettingsCard embedded />}
        {section.id === 'ai' && <AISettingsCard embedded />}
        {section.id === 'server-backup' && <ServerBackupSettingsCard embedded />}
        {section.id === 'nas-backup' && <NasBackupSettingsCard embedded />}
        {section.id === 'exchange' && <ExchangeSettingsCard embedded />}
        {section.id === 'zabbix' && <ZabbixSettingsCard embedded />}
        {section.id === 'server-config' && <ServerConfigPanel />}
      </div>
    </div>
  )
}

export function Settings() {
  const [params] = useSearchParams()
  const sectionId = params.get('section')
  const section = SECTIONS.find((item) => item.id === sectionId && !item.href)

  if (section) {
    return <SectionDetail section={section} />
  }

  return <SettingsOverview />
}
